"use client";

import React from "react";
import { CircleCheck as CheckCircle2, ShieldCheck, Award, Loader as Loader2, MapPin } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const trustBadges = [{
  icon: Award,
  text: "ISA Certified Arborists"
}, {
  icon: ShieldCheck,
  text: "Fully Licensed & Insured"
}, {
  icon: CheckCircle2,
  text: "Safety First Protocol"
}];
function AssessmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-16 bg-accent/20 text-accent rounded-lg flex items-center justify-center mb-4">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="text-2xl font-bold font-default text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-sm">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-accent text-foreground hover:bg-accent hover:text-accent-foreground">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background border-2 border-border focus-visible:border-accent focus-visible:ring-accent/20 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 123-4567" className="bg-background border-2 border-border focus-visible:border-accent focus-visible:ring-accent/20 h-12" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-semibold">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className="bg-background border-2 border-border focus-visible:border-accent focus-visible:ring-accent/20 h-12" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="address" rules={{
      required: "Property address is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-semibold">Property Address</FormLabel>
            <FormControl>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-muted-foreground" />
                <Input placeholder="123 Main St, Fort Worth, TX" className="bg-background border-2 border-border focus-visible:border-accent focus-visible:ring-accent/20 h-12 pl-10" {...field} />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="details" rules={{
      required: "Please provide some details about your project"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-semibold">Project Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe the tree services you need (e.g., removal, pruning, health assessment)..." className="bg-background border-2 border-border focus-visible:border-accent focus-visible:ring-accent/20 min-h-[120px] resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5">
        {isSubmitting ? <>
            <Loader2 className="animate-spin mr-2 size-5" />
            Processing Request...
          </> : 'Professional Arborist Assessment'}
      </Button>
    </Form>;
}
export default function ScheduleAssessment() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="scheduleassessment" className="bg-background py-20 md:py-32 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-accent/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-5 space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-accent/10 text-primary font-semibold text-sm border border-accent/20">
                <span className="size-2 rounded-lg bg-accent animate-pulse" />
                Available for On-Site Visits
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-foreground leading-tight">
                Schedule Your Free Arborist <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/70">Consultation & Estimate</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                Receive a professional on-site evaluation from our ISA Certified Arborists. We provide personalized recommendations and a transparent, no-pressure assessment of your property's needs, followed by a detailed estimate for your project.
              </p>
            </div>

            <div className="space-y-4 pt-4">
              <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider">Why Choose Arbor Pro</h4>
              <div className="flex flex-col gap-3">
                {trustBadges.map((badge, i) => <div key={i} data-index={i} className="flex items-center gap-3 bg-card/50 p-3 rounded-lg border border-border/50 w-fit pr-6">
                    <div className="bg-accent/20 p-2 rounded-md text-primary">
                      <badge.icon className="size-5" />
                    </div>
                    <span className="font-medium text-foreground">{badge.text}</span>
                  </div>)}
              </div>
            </div>
          </div>

          {}
          <div ref={formRef} className={`lg:col-span-7 lg:pl-10 transition-all duration-1000 delay-200 ease-out ${isFormVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="bg-card rounded-2xl shadow-2xl border border-border relative overflow-hidden">
              {}
              <div className="absolute top-0 left-0 w-full h-1.5 bg-accent" />
              
              <div className="p-6 md:p-10">
                <div className="mb-8">
                  <h3 className="text-2xl font-bold font-default text-foreground mb-2">Request a Free Quote</h3>
                  <p className="text-muted-foreground">Fill out the form below and our team will contact you within 24 hours to schedule your assessment.</p>
                </div>

                <FormProvider formId="estimate_form_get_quote" sectionName="ScheduleAssessment" defaultValues={{
                name: "",
                phone: "",
                email: "",
                address: "",
                details: ""
              }} validationRules={{
                name: {
                  required: true
                },
                phone: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                address: {
                  required: true
                },
                details: {
                  required: true
                }
              }} submitText="Get Your Free Estimate" successMessage="Thank you for reaching out! We have received your request and will contact you shortly to schedule your assessment.">
                  <AssessmentForm />
                </FormProvider>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}